import { AlertCircle, Check, Copy, Info } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch, useFetch } from 'utils/fetch'

function S3RecordingConfig({ encryptedKey }) {
  const [roleArn, setRoleArn] = useState('')
  const [copiedField, setCopiedField] = useState(null)
  const [showSetupInstructions, setShowSetupInstructions] = useState(false)

  // Fetch S3 configuration using useFetch
  const { data: s3Config, isLoading: loading, error, refetch } = useFetch('/byot/s3_config', {
    headers: {
      encrypted_key: encryptedKey,
    },
    enabled: !!encryptedKey,
  })

  // Update roleArn when s3Config changes
  useEffect(() => {
    if (s3Config) {
      setRoleArn(s3Config.s3_role_arn || '')
    }
  }, [s3Config])

  // Save S3 configuration mutation
  const saveConfigMutation = useMutation({
    mutationFn: async () => {
      const { data } = await $fetch('/byot/s3_config', {
        method: 'POST',
        headers: {
          encrypted_key: encryptedKey,
        },
        body: {
          s3_role_arn: roleArn || null,
        },
      })
      return data
    },
    onSuccess: (data) => {
      toast.success('S3 configuration saved successfully')
      refetch()
    },
    onError: (error) => {
      console.error('Failed to save S3 config:', error)
      toast.error('Failed to save S3 configuration')
    },
  })

  // Generate external ID mutation
  const generateExternalIdMutation = useMutation({
    mutationFn: async () => {
      const { data } = await $fetch('/byot/s3_config/generate_external_id', {
        method: 'POST',
        headers: {
          encrypted_key: encryptedKey,
        },
      })
      return data
    },
    onSuccess: () => {
      toast.success('External ID generated successfully')
      refetch()
    },
    onError: (error) => {
      console.error('Failed to generate external ID:', error)
      toast.error('Failed to generate external ID')
    },
  })

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
      toast.success(`${field} copied to clipboard`)
    }
    catch (err) {
      toast.error(`Failed to copy ${field}`)
    }
  }

  const generateTrustPolicy = () => {
    if (!s3Config?.s3_external_id)
      return ''

    const accountId = s3Config?.bland_account_id || '344997732581'
    return JSON.stringify({
      Version: '2012-10-17',
      Statement: [{
        Effect: 'Allow',
        Principal: {
          AWS: `arn:aws:iam::${accountId}:root`,
        },
        Action: 'sts:AssumeRole',
        Condition: {
          StringEquals: {
            'sts:ExternalId': s3Config.s3_external_id,
          },
        },
      }],
    }, null, 2)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-xs text-gray-500">Loading S3 configuration...</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-y-3">
      {error && (
        <div className="flex items-start gap-x-2 rounded-md bg-red-50 p-2.5">
          <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0 text-red-500" />
          <p className="text-xs text-red-700">Failed to fetch S3 configuration. Please try again.</p>
        </div>
      )}

      <p className="text-xs text-gray-600">
        Configure S3 access for recordings stored in your own S3 bucket.
        This allows Bland to access your Twilio recordings when they're stored externally.
      </p>

      {!s3Config?.has_s3_config && !s3Config?.s3_external_id && (
        <div className="rounded-md bg-blue-50 p-3">
          <p className="text-xs text-blue-700">
            S3 storage is not configured. If your Twilio account stores recordings in S3,
            click "Generate External ID" below to get started.
          </p>
          <button
            type="button"
            onClick={() => generateExternalIdMutation.mutate()}
            className={`
              mt-2 rounded bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white
              hover:bg-blue-700
              focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              focus:outline-hidden
              disabled:cursor-not-allowed disabled:opacity-50
            `}
            disabled={generateExternalIdMutation.isPending}
          >
            {generateExternalIdMutation.isPending ? 'Generating...' : 'Generate External ID'}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {s3Config?.s3_external_id && (
          <>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Your AWS Role ARN
              </label>
              <input
                type="text"
                value={roleArn}
                onChange={e => setRoleArn(e.target.value)}
                placeholder="arn:aws:iam::123456789012:role/YourRoleName"
                className={`
                  block w-full rounded border border-gray-300 px-2 py-1.5
                  text-xs shadow-xs
                  focus:border-indigo-500 focus:ring-indigo-500
                  focus:outline-hidden
                `}
              />
              <p className="mt-1 text-2xs text-gray-500">
                Enter the ARN of the IAM role you created for Bland to assume
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                External ID (Read-only)
              </label>
              <div className="flex items-center gap-x-2">
                <input
                  type="text"
                  value={s3Config?.s3_external_id || 'Generating...'}
                  readOnly
                  className={`
                    block w-full rounded border border-gray-300 bg-gray-50 px-2
                    py-1.5 text-xs shadow-xs
                  `}
                />
                <button
                  onClick={() => copyToClipboard(s3Config?.s3_external_id, 'External ID')}
                  className={`
                    text-gray-400
                    hover:text-gray-600
                  `}
                  title="Copy External ID"
                  disabled={!s3Config?.s3_external_id}
                >
                  {copiedField === 'External ID'
                    ? (
                        <Check className="h-3 w-3 text-green-500" />
                      )
                    : (
                        <Copy className="h-3 w-3" />
                      )}
                </button>
              </div>
              <p className="mt-1 text-2xs text-gray-500">
                Use this External ID in your IAM role's trust policy
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Bland Account ID (Read-only)
              </label>
              <div className="flex items-center gap-x-2">
                <input
                  type="text"
                  value={s3Config?.bland_account_id || '344997732581'}
                  readOnly
                  className={`
                    block w-full rounded border border-gray-300 bg-gray-50
                    px-2 py-1.5 text-xs shadow-xs
                  `}
                />
                <button
                  onClick={() => copyToClipboard(s3Config?.bland_account_id || '344997732581', 'Bland Account ID')}
                  className={`
                    text-gray-400
                    hover:text-gray-600
                  `}
                  title="Copy Bland Account ID"
                >
                  {copiedField === 'Bland Account ID'
                    ? (
                        <Check className="h-3 w-3 text-green-500" />
                      )
                    : (
                        <Copy className="h-3 w-3" />
                      )}
                </button>
              </div>
              <p className="mt-1 text-2xs text-gray-500">
                Use this Account ID in your IAM role's trust policy
              </p>
            </div>
          </>
        )}

        <div className="flex items-center gap-x-2">
          {s3Config?.s3_external_id && (
            <button
              type="button"
              onClick={() => saveConfigMutation.mutate()}
              disabled={saveConfigMutation.isPending || !roleArn}
              className={`
                rounded bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white
                hover:bg-blue-700
                focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                focus:outline-hidden
                disabled:cursor-not-allowed disabled:opacity-50
              `}
            >
              {saveConfigMutation.isPending ? 'Saving...' : 'Save Configuration'}
            </button>
          )}

          <button
            onClick={() => setShowSetupInstructions(!showSetupInstructions)}
            className={`
              rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs
              font-medium text-gray-700
              hover:bg-gray-50
              focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
              focus:outline-hidden
            `}
          >
            {showSetupInstructions ? 'Hide' : 'Show'}
            {' '}
            Setup Instructions
          </button>
        </div>
      </div>

      {showSetupInstructions && s3Config?.s3_external_id && (
        <div className="mt-3 space-y-3 rounded-md bg-gray-50 p-3">
          <h4 className="text-xs font-semibold text-gray-700">
            AWS IAM Setup Instructions
          </h4>

          <div>
            <p className="mb-1 text-xs font-medium text-gray-700">
              1. Create an IAM Role with this trust policy:
            </p>
            <div className="relative">
              <pre className={`
                overflow-x-auto rounded bg-gray-800 p-2 text-2xs text-gray-100
              `}
              >
                {generateTrustPolicy()}
              </pre>
              <button
                onClick={() => copyToClipboard(generateTrustPolicy(), 'Trust Policy')}
                className={`
                  absolute top-1 right-1 text-gray-400
                  hover:text-gray-200
                `}
                title="Copy trust policy"
              >
                {copiedField === 'Trust Policy'
                  ? (
                      <Check className="h-3 w-3 text-green-400" />
                    )
                  : (
                      <Copy className="h-3 w-3" />
                    )}
              </button>
            </div>
          </div>

          <div>
            <p className="mb-1 text-xs font-medium text-gray-700">
              2. Attach a policy granting S3 read access:
            </p>
            <pre className={`
              overflow-x-auto rounded bg-gray-800 p-2 text-2xs text-gray-100
            `}
            >
              {`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": [
      "s3:GetObject",
      "s3:GetObjectVersion"
    ],
    "Resource": "arn:aws:s3:::YOUR_BUCKET/*"
  }]
}`}
            </pre>
          </div>

          <div className="flex items-start gap-x-1">
            <Info className="mt-0.5 h-3 w-3 flex-shrink-0 text-blue-500" />
            <p className="text-2xs text-gray-600">
              Replace YOUR_BUCKET with your actual S3 bucket name.
              After creating the role, copy its ARN and paste it in the field above.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default S3RecordingConfig
